import {useConfigStore} from '../../../store/reportStore/configStore';

let pdfExportDepsPromise: Promise<{jsPDF: any; html2canvas: any}> | null = null;

export const loadPdfExportDeps = async (): Promise<{
  jsPDF: any;
  html2canvas: any;
}> => {
  if (!pdfExportDepsPromise) {
    pdfExportDepsPromise = Promise.all([
      import('jspdf'),
      import('html2canvas')
    ]).then(([jspdfModule, html2canvasModule]) => ({
      jsPDF: jspdfModule.default,
      html2canvas: html2canvasModule.default
    }));
  }

  return await pdfExportDepsPromise;
};

const inchesToMm = (inches: number): number => inches * 25.4;

const resolvePageDimensionsMm = (
  pageOrientation: string
): {width: number; height: number} => {
  const isLandscape = pageOrientation === 'landscape';

  return {
    width: isLandscape ? 297 : 210,
    height: isLandscape ? 210 : 297
  };
};

const normalizeMarginsInches = (
  pageMargins: any = {}
): {top: number; right: number; bottom: number; left: number} => ({
  top: Math.max(0, Math.min(3, Number(pageMargins?.top ?? 0.5))),
  right: Math.max(0, Math.min(3, Number(pageMargins?.right ?? 0.5))),
  bottom: Math.max(0, Math.min(3, Number(pageMargins?.bottom ?? 0.5))),
  left: Math.max(0, Math.min(3, Number(pageMargins?.left ?? 0.5)))
});

const parsePageSelection = (
  selection: string | undefined,
  totalPages: number
): number[] => {
  const allPages = Array.from({length: totalPages}, (_, idx) => idx);
  const raw = (selection ?? 'all').trim().toLowerCase();
  if (!raw || raw === 'all') return allPages;

  const chosen = new Set<number>();
  const tokens = raw
    .split(',')
    .map((token) => token.trim())
    .filter(Boolean);

  tokens.forEach((token) => {
    const rangeMatch = token.match(/^(\d+)\s*-\s*(\d+)$/);
    if (rangeMatch) {
      const start = Math.max(1, Number(rangeMatch[1]));
      const end = Math.max(1, Number(rangeMatch[2]));
      const from = Math.min(start, end);
      const to = Math.max(start, end);
      for (let p = from; p <= to; p++) {
        if (p <= totalPages) chosen.add(p - 1);
      }

      return;
    }

    const pageNum = Number(token);
    if (Number.isFinite(pageNum) && pageNum >= 1 && pageNum <= totalPages) {
      chosen.add(pageNum - 1);
    }
  });

  const indices = Array.from(chosen).sort((a, b) => a - b);

  return indices.length > 0 ? indices : allPages;
};

/**
 * Export the document to PDF
 * @param {Array} pages - Array of page content from the document
 * @param {string} pageOrientation - Page orientation ('portrait' | 'landscape')
 * @param {Object} _pageMargins - Page margins
 * @param {string} filename - Output filename
 */
export const exportToPDF = async (
  pages: any,
  pageOrientation = useConfigStore.getState().pageOrientation,
  _pageMargins: any = {},
  filename = 'esg-document.pdf',
  options: any = {}
): Promise<{success: boolean; message: string; url?: string}> => {
  try {
    const size = resolvePageDimensionsMm(pageOrientation);

    const deps = await loadPdfExportDeps();
    const JsPDF = deps.jsPDF;
    const html2canvas = deps.html2canvas;

    // Create PDF with specified page size
    const pdf = new JsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: [size.width, size.height]
    });

    // Capture rendered editor pages directly so the PDF matches on-screen layout.
    const pageElements = document.querySelectorAll('.paginated-page');

    if (pageElements.length === 0) {
      throw new Error('No pages found to export');
    }

    const selectedPageIndices = parsePageSelection(
      options.pageSelection,
      pageElements.length
    );

    // Process each page
    for (let i = 0; i < selectedPageIndices.length; i++) {
      const pageElement = pageElements[selectedPageIndices[i]];

      // Capture the page as canvas
      const pageNode = pageElement as HTMLElement;
      const canvas = await html2canvas(pageNode, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        width: pageNode.offsetWidth,
        height: pageNode.offsetHeight,
        scrollX: 0,
        scrollY: 0
      } as any);

      const imgData = canvas.toDataURL('image/png');
      // Always fit to exact target paper size to prevent manual print scaling.
      const imgWidth = size.width;
      const imgHeight = size.height;

      // Add new page if not the first one
      if (i > 0) {
        pdf.addPage([size.width, size.height]);
      }

      // Add image to PDF
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
    }

    if (options.mode === 'open') {
      const blob = pdf.output('blob') as Blob;
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank', 'noopener,noreferrer');

      return {success: true, message: 'PDF opened in viewer', url: blobUrl};
    }

    // Save the PDF
    pdf.save(filename);

    return {success: true, message: 'PDF exported successfully'};
  } catch (error: any) {
    console.error('Error exporting PDF:', error);

    return {success: false, message: error.message};
  }
};

/**
 * Alternative method: Export using direct content rendering (better formatting control)
 * @param {Array} allContent - Slate.js content array
 * @param {string} pageOrientation - Page orientation ('portrait' | 'landscape')
 * @param {string} filename - Output filename
 */
export const exportContentToPDF = async (
  allContent: any,
  pageOrientation = useConfigStore.getState().pageOrientation,
  filename = 'esg-document.pdf',
  pageMargins: any = {}
): Promise<{success: boolean; message: string}> => {
  try {
    const size = resolvePageDimensionsMm(pageOrientation);
    const deps = await loadPdfExportDeps();
    const JsPDF = deps.jsPDF;

    const pdf = new JsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: [size.width, size.height]
    });

    const normalizedMargins = normalizeMarginsInches(pageMargins);
    const margins = {
      top: inchesToMm(normalizedMargins.top),
      right: inchesToMm(normalizedMargins.right),
      bottom: inchesToMm(normalizedMargins.bottom),
      left: inchesToMm(normalizedMargins.left)
    };
    const pageWidth = size.width - margins.left - margins.right;

    let yPosition = margins.top;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    let currentPage = 1;

    // Helper function to add new page
    const addNewPage = (): void => {
      pdf.addPage([size.width, size.height]);
      currentPage++;
      yPosition = margins.top;
    };

    // Helper function to check if we need a new page
    const checkPageBreak = (requiredHeight: any): boolean => {
      if (yPosition + requiredHeight > size.height - margins.bottom) {
        addNewPage();

        return true;
      }

      return false;
    };

    // Process each content node
    allContent.forEach((node: any, _index: number) => {
      const text =
        node.children?.map((child: any) => child.text).join('') || '';

      // Set font based on node type
      if (node.type === 'heading-one') {
        pdf.setFontSize(24);
        pdf.setFont(undefined, 'bold');
        checkPageBreak(15);
        pdf.text(text, margins.left, yPosition);
        yPosition += 15;
      } else if (node.type === 'heading-two') {
        pdf.setFontSize(18);
        pdf.setFont(undefined, 'bold');
        checkPageBreak(12);
        pdf.text(text, margins.left, yPosition);
        yPosition += 12;
      } else if (node.type === 'block-quote') {
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'italic');
        checkPageBreak(10);
        const lines = pdf.splitTextToSize(text, pageWidth - 20);
        pdf.text(lines, margins.left + 10, yPosition);
        yPosition += lines.length * 7;
      } else {
        // Normal paragraph
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'normal');

        if (text.trim()) {
          checkPageBreak(10);
          const lines = pdf.splitTextToSize(text, pageWidth);
          pdf.text(lines, margins.left, yPosition);
          yPosition += lines.length * 7;
        } else {
          yPosition += 5; // Empty line spacing
        }
      }

      yPosition += 3; // Add spacing between elements
    });

    // Add page numbers
    const totalPages = pdf.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);
      pdf.setFontSize(10);
      pdf.setFont(undefined, 'normal');
      pdf.text(`Page ${i} of ${totalPages}`, size.width / 2, size.height - 10, {
        align: 'center'
      });
    }

    pdf.save(filename);

    return {success: true, message: 'PDF exported successfully'};
  } catch (error: any) {
    console.error('Error exporting PDF:', error);

    return {success: false, message: error.message};
  }
};
